import type { Messages } from '../i18n/messages';

/**
 * Environment/Resource/Secret gitops config editors (entity tabs). Overlay
 * names, env var keys/values, Azure secret refs, resource tier/quantity
 * values (e.g. `nano`, `250m`) and task ids are gitops/server data and stay
 * raw — only the editor chrome (toolbar, table headers, empty states,
 * buttons, snackbars) is keyed here.
 */
export const configurationMessages: Messages = {
  // Entity-content tab titles (non-component config, evaluated once —
  // see the i18n module's non-component-code note).
  'configuration.environmentEntityContent.title': {
    en: 'Environment',
    fun: 'Environment',
  },
  'configuration.resourceEntityContent.title': {
    en: 'Resources',
    fun: 'Resources',
  },
  'configuration.secretEntityContent.title': { en: 'Secret', fun: 'Secret' },

  // EnvironmentConfigTab
  'configuration.environmentConfigTab.notLinked': {
    en: 'This component is not linked to a gitops service.',
    fun: 'This component has no gitops service to talk to.',
  },
  'configuration.environmentConfigTab.loadError.title': {
    en: 'Failed to load config',
    fun: 'Config fetch fumbled',
  },
  'configuration.environmentConfigTab.submitSuccess.title': {
    en: 'Configuration update started',
    fun: 'Config update is rolling',
  },
  'configuration.environmentConfigTab.submitSuccess.viewTask': {
    en: 'View task',
    fun: 'View task',
  },
  'configuration.environmentConfigTab.submitError.title': {
    en: 'Submission failed',
    fun: 'Submission tripped and fell',
  },

  // EnvironmentConfigView
  'configuration.environmentConfigView.overlayTabsLabel': {
    en: 'Environment overlay',
    fun: 'Environment overlay',
  },
  'configuration.environmentConfigView.errorTitle': {
    en: 'Error',
    fun: 'Uh oh',
  },
  'configuration.environmentConfigView.cancel': {
    en: 'Cancel',
    fun: 'Cancel',
  },
  'configuration.environmentConfigView.submit': {
    en: 'Submit changes',
    fun: 'Ship changes',
  },
  'configuration.environmentConfigView.submitting': {
    en: 'Submitting…',
    fun: 'Shipping…',
  },

  // EnvVarEditor
  'configuration.envVarEditor.filterPlaceholder': {
    en: 'Filter keys…',
    fun: 'Filter keys…',
  },
  'configuration.envVarEditor.filterAriaLabel': {
    en: 'Filter keys',
    fun: 'Filter keys',
  },
  'configuration.envVarEditor.overridden': {
    en: 'Overridden',
    fun: 'Overridden',
  },
  'configuration.envVarEditor.inherited': {
    en: 'Inherited',
    fun: 'Inherited',
  },
  'configuration.envVarEditor.refreshAriaLabel': {
    en: 'Refresh from gitops',
    fun: 'Refresh from gitops',
  },
  'configuration.envVarEditor.loading': {
    en: 'Loading variables…',
    fun: 'Rounding up variables…',
  },
  'configuration.envVarEditor.emptyNoVariables': {
    en: 'No variables configured',
    fun: 'Nothing configured yet',
  },
  'configuration.envVarEditor.addVariable': {
    en: 'Add variable',
    fun: 'Add variable',
  },
  'configuration.envVarEditor.emptyNoMatch': {
    en: 'No variables match the current filters',
    fun: 'Nothing matches those filters',
  },
  'configuration.envVarEditor.columnKey': { en: 'Key', fun: 'Key' },
  'configuration.envVarEditor.columnValue': { en: 'Value', fun: 'Value' },
  'configuration.envVarEditor.columnSource': { en: 'Source', fun: 'Source' },
  'configuration.envVarEditor.actionsAriaLabel': {
    en: 'Actions',
    fun: 'Actions',
  },
  'configuration.envVarEditor.keyPlaceholder': { en: 'KEY', fun: 'KEY' },
  'configuration.envVarEditor.keyAriaLabel': {
    en: 'Key for row {row}',
    fun: 'Key for row {row}',
  },
  'configuration.envVarEditor.valuePlaceholder': {
    en: 'value',
    fun: 'value',
  },
  'configuration.envVarEditor.valueAriaLabel': {
    en: 'Value for row {row}',
    fun: 'Value for row {row}',
  },
  'configuration.envVarEditor.sourceLabel.override': {
    en: 'config.env',
    fun: 'config.env',
  },
  'configuration.envVarEditor.sourceLabel.base': { en: 'base', fun: 'base' },
  'configuration.envVarEditor.sourceLabel.common': {
    en: 'common',
    fun: 'common',
  },
  'configuration.envVarEditor.diffLabel.added': {
    en: 'Added',
    fun: 'Added',
  },
  'configuration.envVarEditor.diffLabel.changed': {
    en: 'Changed',
    fun: 'Changed',
  },
  'configuration.envVarEditor.diffLabel.deleted': {
    en: 'Deleted',
    fun: 'Deleted',
  },
  'configuration.envVarEditor.removeAriaLabel': {
    en: 'Remove row {row}',
    fun: 'Remove row {row}',
  },
  'configuration.envVarEditor.overrideButton': {
    en: 'Override',
    fun: 'Override',
  },
  'configuration.envVarEditor.overrideAriaLabel': {
    en: 'Override {key}',
    fun: 'Override {key}',
  },
  'configuration.envVarEditor.restoreButton': {
    en: 'Restore',
    fun: 'Restore',
  },
  'configuration.envVarEditor.restoreAriaLabel': {
    en: 'Restore {key}',
    fun: 'Restore {key}',
  },

  // ResourceConfigTab
  'configuration.resourceConfigTab.notAvailable': {
    en: 'Resource settings are only available for services and AI services.',
    fun: 'Resource settings only show up for services and AI services.',
  },
  'configuration.resourceConfigTab.loadError.title': {
    en: 'Failed to load resource config',
    fun: 'Resource config fetch fumbled',
  },
  'configuration.resourceConfigTab.submitSuccess.title': {
    en: 'Resource update started',
    fun: 'Resource update is rolling',
  },
  'configuration.resourceConfigTab.submitSuccess.viewTask': {
    en: 'View task',
    fun: 'View task',
  },
  'configuration.resourceConfigTab.submitError.title': {
    en: 'Submission failed',
    fun: 'Submission tripped and fell',
  },

  // ResourceConfigView
  'configuration.resourceConfigView.overlayTabsLabel': {
    en: 'Resource overlay',
    fun: 'Resource overlay',
  },
  'configuration.resourceConfigView.refreshAriaLabel': {
    en: 'Refresh from gitops',
    fun: 'Refresh from gitops',
  },
  'configuration.resourceConfigView.errorTitle': {
    en: 'Error',
    fun: 'Uh oh',
  },
  'configuration.resourceConfigView.loading': {
    en: 'Loading config…',
    fun: 'Rounding up the config…',
  },
  'configuration.resourceConfigView.cpuTierLabel': {
    en: 'CPU tier',
    fun: 'CPU tier',
  },
  'configuration.resourceConfigView.memoryTierLabel': {
    en: 'Memory tier',
    fun: 'Memory tier',
  },
  'configuration.resourceConfigView.tierOption.keepCurrent': {
    en: 'keep current',
    fun: 'keep current',
  },
  'configuration.resourceConfigView.tierOption.custom': {
    en: 'custom (enter values below)',
    fun: 'custom (enter values below)',
  },
  'configuration.resourceConfigView.notSet': {
    en: 'not set',
    fun: 'not set',
  },
  'configuration.resourceConfigView.currentTier': {
    en: 'Current: {tier}',
    fun: 'Right now: {tier}',
  },
  'configuration.resourceConfigView.currentCount': {
    en: 'Current: {count}',
    fun: 'Right now: {count}',
  },
  'configuration.resourceConfigView.customCpuRequest.label': {
    en: 'Custom CPU request',
    fun: 'Custom CPU request',
  },
  'configuration.resourceConfigView.customCpuLimit.label': {
    en: 'Custom CPU limit',
    fun: 'Custom CPU limit',
  },
  'configuration.resourceConfigView.customMemoryRequest.label': {
    en: 'Custom memory request',
    fun: 'Custom memory request',
  },
  'configuration.resourceConfigView.customMemoryLimit.label': {
    en: 'Custom memory limit',
    fun: 'Custom memory limit',
  },
  'configuration.resourceConfigView.minReplicasLabel': {
    en: 'Min replicas',
    fun: 'Min replicas',
  },
  'configuration.resourceConfigView.maxReplicasLabel': {
    en: 'Max replicas',
    fun: 'Max replicas',
  },
  'configuration.resourceConfigView.hpaDisabledMessage': {
    en: "Autoscaling (HPA) is not enabled in this service's base; replica settings unavailable.",
    fun: "Autoscaling (HPA) is off in this service's base, so replica settings sit this one out.",
  },
  'configuration.resourceConfigView.cancel': {
    en: 'Cancel',
    fun: 'Cancel',
  },
  'configuration.resourceConfigView.submit': {
    en: 'Submit changes',
    fun: 'Ship changes',
  },
  'configuration.resourceConfigView.submitting': {
    en: 'Submitting…',
    fun: 'Shipping…',
  },

  // SecretConfigTab
  'configuration.secretConfigTab.notLinked': {
    en: 'This component is not linked to a gitops service.',
    fun: 'This component has no gitops service to talk to.',
  },
  'configuration.secretConfigTab.overlayTabsLabel': {
    en: 'Secret overlay',
    fun: 'Secret overlay',
  },
  'configuration.secretConfigTab.errorTitle': {
    en: 'Error',
    fun: 'Uh oh',
  },
  'configuration.secretConfigTab.loadError.title': {
    en: 'Failed to load secrets',
    fun: 'Secret fetch fumbled',
  },
  'configuration.secretConfigTab.submitSuccess.title': {
    en: 'Secret update started',
    fun: 'Secret update is rolling',
  },
  'configuration.secretConfigTab.submitSuccess.viewTask': {
    en: 'View task',
    fun: 'View task',
  },
  'configuration.secretConfigTab.submitError.title': {
    en: 'Submission failed',
    fun: 'Submission tripped and fell',
  },
  'configuration.secretConfigTab.cancel': { en: 'Cancel', fun: 'Cancel' },
  'configuration.secretConfigTab.submit': {
    en: 'Submit changes',
    fun: 'Ship changes',
  },
  'configuration.secretConfigTab.submitting': {
    en: 'Submitting…',
    fun: 'Shipping…',
  },

  // SecretRefEditor
  'configuration.secretRefEditor.filterPlaceholder': {
    en: 'Filter secrets…',
    fun: 'Filter secrets…',
  },
  'configuration.secretRefEditor.filterAriaLabel': {
    en: 'Filter secrets',
    fun: 'Filter secrets',
  },
  'configuration.secretRefEditor.overridden': {
    en: 'Overridden',
    fun: 'Overridden',
  },
  'configuration.secretRefEditor.inherited': {
    en: 'Inherited',
    fun: 'Inherited',
  },
  'configuration.secretRefEditor.refreshAriaLabel': {
    en: 'Refresh from gitops',
    fun: 'Refresh from gitops',
  },
  'configuration.secretRefEditor.azureHint': {
    en: "Values live in Azure Key Vault — use the Azure link to view or set a secret's value.",
    fun: "Values live in Azure Key Vault — hit the Azure link to peek at or set a secret's value.",
  },
  'configuration.secretRefEditor.loading': {
    en: 'Loading secrets…',
    fun: 'Rounding up secrets…',
  },
  'configuration.secretRefEditor.emptyNoSecrets': {
    en: 'No secrets configured',
    fun: 'Nothing configured yet',
  },
  'configuration.secretRefEditor.addSecret': {
    en: 'Add secret',
    fun: 'Add secret',
  },
  'configuration.secretRefEditor.emptyNoMatch': {
    en: 'No secrets match the current filters',
    fun: 'Nothing matches those filters',
  },
  'configuration.secretRefEditor.columnKey': { en: 'Key', fun: 'Key' },
  'configuration.secretRefEditor.columnAzureRef': {
    en: 'Azure secret (ref)',
    fun: 'Azure secret (ref)',
  },
  'configuration.secretRefEditor.columnSource': {
    en: 'Source',
    fun: 'Source',
  },
  'configuration.secretRefEditor.actionsAriaLabel': {
    en: 'Actions',
    fun: 'Actions',
  },
  'configuration.secretRefEditor.keyPlaceholder': {
    en: 'secretKey',
    fun: 'secretKey',
  },
  'configuration.secretRefEditor.keyAriaLabel': {
    en: 'Key for row {row}',
    fun: 'Key for row {row}',
  },
  'configuration.secretRefEditor.azureRefPlaceholder': {
    en: 'azure-keyvault-secret-name',
    fun: 'azure-keyvault-secret-name',
  },
  'configuration.secretRefEditor.azureRefAriaLabel': {
    en: 'Azure secret ref for row {row}',
    fun: 'Azure secret ref for row {row}',
  },
  'configuration.secretRefEditor.sourceLabel.override': {
    en: 'secrets.yaml',
    fun: 'secrets.yaml',
  },
  'configuration.secretRefEditor.sourceLabel.base': {
    en: 'base',
    fun: 'base',
  },
  'configuration.secretRefEditor.diffLabel.added': {
    en: 'Added',
    fun: 'Added',
  },
  'configuration.secretRefEditor.diffLabel.changed': {
    en: 'Changed',
    fun: 'Changed',
  },
  'configuration.secretRefEditor.diffLabel.deleted': {
    en: 'Deleted',
    fun: 'Deleted',
  },
  'configuration.secretRefEditor.viewInAzureAriaLabel': {
    en: 'View/set value in Azure',
    fun: 'View/set value in Azure',
  },
  'configuration.secretRefEditor.removeAriaLabel': {
    en: 'Remove row {row}',
    fun: 'Remove row {row}',
  },
  'configuration.secretRefEditor.overrideButton': {
    en: 'Override',
    fun: 'Override',
  },
  'configuration.secretRefEditor.overrideAriaLabel': {
    en: 'Override {key}',
    fun: 'Override {key}',
  },
  'configuration.secretRefEditor.restoreButton': {
    en: 'Restore',
    fun: 'Restore',
  },
  'configuration.secretRefEditor.restoreAriaLabel': {
    en: 'Restore {key}',
    fun: 'Restore {key}',
  },
};
